import {
  Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { usePortfolioTrend } from "../lib/hooks";
import Spinner from "./Spinner";

function fmt$(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

interface ChartsProps {
  showUserOnly?: boolean;
}

export default function InvestmentLineChart({ showUserOnly = true }: ChartsProps) {
  const { data, loading } = usePortfolioTrend(showUserOnly);

  if (loading) return <Spinner />;
  if (!data?.length) return null;

  return (
    <div style={S.card}>
      <h3 style={S.title}>
        {showUserOnly ? "My Monthly Portfolio Performance" : "Global Fund Monthly Performance"}
      </h3>

      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 10, fill: "var(--text-secondary)" }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 10, fill: "var(--text-secondary)" }}
            tickFormatter={(v) => fmt$(v)}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            contentStyle={{
              background: "#111",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              color: "#f0ede8",
              fontSize: 12,
            }}
            formatter={(v: number) => [fmt$(v), ""]}
          />
          <Legend
            verticalAlign="top"
            height={36}
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: 12 }}
          />
          <Line
            type="monotone"
            dataKey="total_value"
            name="Total Value"
            stroke="var(--teal-light)"
            strokeWidth={2.5}
            dot={{ r: 2, strokeWidth: 1 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="total_cost"
            name="Invested"
            stroke="#e05a5a"
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={{ r: 2, strokeWidth: 1 }}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 16px",
    boxSizing: "border-box",
  },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 16, letterSpacing: "0.02em" },
};
